import React, { PropTypes, Component } from 'react';
import idx from 'idx';
import Tooltip from 'react-tooltip';
import { reset } from 'redux-form';
import classnames from 'classnames';
import { isMobile } from 'common/utils';
import { isProjectTagline } from 'project/utils';
import { getPackageSelectorFeatures } from 'pap/utils';
import { ELITE_PACKAGE_SLUG, PACKAGE_SELECTOR_DESCRIPTIONS } from 'pap/constants';
import PackageDetails from 'pap/components/common/input_fields/package_details';

class Package extends Component {
  constructor(props) {
    super(props);

    this.handleSave = this.handleSave.bind(this);
    this.save = this.save.bind(this);
    this.confirmElite = this.confirmElite.bind(this);
    this.showDetails = this.showDetails.bind(this);
  }

  showDetails(e) {
    const { pack, displayFullScreen } = this.props;

    e.preventDefault();
    displayFullScreen("package_details", pack);
  }

  confirmElite() {
    this.save();
  }

  handleSave(e) {
    const { input, pack, noEliteConfirmation, onToggleEliteConfirmation } = this.props;

    e.preventDefault();

    /* if selecting an elite package, show confirmation modal */
    if (input.value !== pack.id && !noEliteConfirmation && pack.slug === ELITE_PACKAGE_SLUG) {
      onToggleEliteConfirmation(this.confirmElite);
      return;
    }

    this.save();
  }

  save() {
    const { input, pack, meta, fullScreen, hideFullScreen } = this.props;
    const showingDetails = idx(fullScreen, _ => _.extraData.name) === pack.name;

    if (showingDetails) {
      hideFullScreen();
    }

    /* reset forms where package selector is in, since next steps will rely on the
       selected package features and we want to have a clean slate */
    meta.dispatch(reset('pap_details'));
    meta.dispatch(reset('pap_details_v2'));
    meta.dispatch(reset('pap_review'));
    input.onChange(input.value === pack.id && !showingDetails? null : pack.id);
  }

  renderAwards(showChecks) {
    const { pack } = this.props;
    const includedAwards = pack.awards.slice(0, pack.min_awards);

    return (
      <span>
        { showChecks && <i className="fa fa-check included" /> }
        { includedAwards.map((award, i) => (
          <span key={i}>
            <span className="dollar-sign">$</span>{ award.feature.value }
            { (i !== includedAwards.length - 1)? '/' : ' award' }
          </span>
        )) }
      </span>
    );
  }

  renderRankedTips() {
    const { pack } = this.props;
    const rankedTipsTotal = (pack.package_tips || []).reduce((acc, t) => acc + t.feature.value, 0);

    if (!rankedTipsTotal) {
      return null;
    }

    return (
      <span>
      {' '}  / <span className="dollar-sign">$</span>{rankedTipsTotal} Tips
      </span>
    );
  }

  render() {
    const { pack, input, subCategory, displayFullScreen, extraClasses } = this.props;
    const selected = input.value === pack.id;
    const inactive = !!input.value && !selected;
    const packageClasses = classnames(`package package-${pack.name.toLowerCase()}`, {
      "not-selected": inactive,
      "selected" : selected,
    });
    const actionLabel = (
      selected? <span><i className="fa fa-check-circle"></i> Selected</span> : `Select ${pack.name}`
    );
    const features = getPackageSelectorFeatures({
      packSlug: pack.slug,
      subcatSlug: subCategory.slug,
    });

    return (
      <div className={classnames("columns package-column", extraClasses)}>
        <div className={packageClasses}>
          <div className="package-top">
            <label className="package-name">{ pack.name }</label>

            <dl className="pricing">
              <dt>
                <span className="dollar-sign small">$</span>
                <span className="dollar-amount">{ Math.floor(pack.base_price) }</span>
              </dt>
              <dd className="award-amount">
                <div className="including">including</div>
                { this.renderAwards() }
                { this.renderRankedTips() }
              </dd>
            </dl>
          </div>
          <div className="package-description">
            { PACKAGE_SELECTOR_DESCRIPTIONS[pack.slug] }
          </div>
          <div className="feature-labels">
            { features.map((f, i) => {

              let label = <i className="fa fa-minus" />;
              if (f.optional) {
                label = <span className="text">optional</span>;
              } else if (f.included) {
                label = (
                  f.includedLabel?
                  <span className="text included">{ f.includedLabel }</span> :
                  <i className="fa fa-check" />
                );
              }

              return <div className="cell" key={i}>{ label }</div>;
            }) }
          </div>
          <div className="package-action">
            <button className="btn btn-primary btn-block form-selector" onClick={this.handleSave}>
              { actionLabel }
            </button>
          </div>
          <button className="btn btn-md btn-text btn-package-details" onClick={this.showDetails}
              disabled={inactive}>
            Full details
          </button>
        </div>

        { !!displayFullScreen && <PackageDetails {...this.props} onSelect={this.handleSave} /> }
      </div>
    );
  }
}

Package.propTypes = {
  input: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  pack: PropTypes.object.isRequired,
  noEliteConfirmation: PropTypes.bool,
  onToggleEliteConfirmation: PropTypes.func.isRequired,
  subCategory: PropTypes.object.isRequired,
  displayFullScreen: PropTypes.func,
  hideFullScreen: PropTypes.func,
  fullScreen: PropTypes.object,
  extraClasses: PropTypes.string,
};

const FeatureNames = ({ subCategory, extraClasses }) => {
  const features = getPackageSelectorFeatures({
    subcatSlug: subCategory.slug,
  });

  return (
    <div className={classnames("columns feature-names", extraClasses)}>
      { features.map((f, i) => (
      <div key={i} className="cell">
        { f.name }

        { !!f.tooltipContent &&
        <i data-tip={true} data-for={`tooltip-${i}`} data-event={isMobile()? "click" : undefined}
          className="fa fa-question-circle" /> }

        { !!f.tooltipContent &&
        <Tooltip class="tooltip" effect="solid" place="right" id={`tooltip-${i}`}>
          { f.tooltipContent }
        </Tooltip> }
      </div>
      )) }
    </div>
  );
}

const Packages = (props) => {
  const { packages, subCategory, input, centeredIntro } = props;

  let entryName = 'design', entryNameVariation = 'design concept';
  if (subCategory.entry_type === 'name') {
    if (isProjectTagline({ sub_category: subCategory.id })) {
      entryName = 'tagline';
      entryNameVariation = 'tagline suggestion';
    } else {
      entryName = 'name';
      entryNameVariation = 'name suggestion';
    }
  }

  if (packages.length === 0) {
    return null;
  }

  return (
    <div className="packages">
      <div className={classnames("row", { "align-center text-center": centeredIntro })}>
        <div className="columns small-16 large-10">
          <p className="intro">
            We offer { packages.length } packages to match any budget. All packages
            include the award, all fees, full copyright ownership of the winning { entryName },
            and unlimited { entryNameVariation }s.
          </p>
        </div>
      </div>

      <div className="row small-up-2 large-up-5 align-center">
        <FeatureNames subCategory={subCategory} extraClasses="show-for-large" />

        { packages.reduce((acc, pack, i) => {
          const inactive = !!input.value && pack.id !== input.value;
          const last = i === packages.length - 1;

          return ([
            ...acc,
            <FeatureNames key={`names_${pack.id}`} subCategory={subCategory}
              extraClasses={classnames("hide-for-large", { inactive, last })} />,
            <Package key={pack.id} {...props} pack={pack} extraClasses={classnames({ last })} />
          ]);
        }, []) }
      </div>
    </div>
  );
}

export default Packages;



// WEBPACK FOOTER //
// ./pap/components/common/input_fields/packages.jsx